import * as config from '@/config/config.json';
import * as calculatorConfig from '@/config/base-config.json';
import { generateSliderSteps } from '@/utils/generateSliderSteps';

export const PDF_FILE_NAME = 'PLEDGE_UNPLEDGE';
export const MAX_PROFILES = 4;
export const PLEDGE_PERIOD_IN_MONTHS = 48; // in months
export const VARIABLE_INCOME_URL =
  'https://www.mom.gov.sg/employment-practices/salary/variable-wage-components';

export const OTHERS_PLEDGE_UNPLEDGE_LABEL = {
  PropertyDetails: 'Property Details',
  FinancingDetails: 'Financing Details',
  Summary: 'Summary',
  PlusAddProfile: '+ Add Profile',
  AdditionalProfile: 'Additional Profile',
  Age: 'Age',
  MonthlyFixedIncome: 'Monthly Fixed Income',
  MonthlyVariableIncome: 'Monthly Variable Income',
  Recognition: 'Recognition',
  TooltipMonthlyVariableIncome:
    'Variable wage components: Annual Wage Supplement (AWS), bonus and variable payment.',
  ReadMore: 'Read more:',
  MonthlyExpenses: 'Monthly Expenses',
  TooltipMonthlyExpenses: 'Car Loans, Housing Loans, Credit Card Loans, & Others',
  PropertyType: 'Property Type',
  PropertySubType: 'Property Sub Type',
  EstimatedPropertyValuation: 'Estimated Property Valuation',
  MediunTermInterestRate: 'Medium Term Interest Rate',
  LoanToValue: 'Loan-to-Value (LTV)',
  LoanTenure: 'Loan Tenure (Years)',
  TotalDebtServicingRatio: 'Total Debt Servicing Ratio (TDSR)',
  MortgageServicingRatio: 'Mortgage Servicing Ratio (MSR)',
  IncomeWeightedAverageAge: 'Income Weighted Average Age',
  Amount: 'Amount',
  TotalIncomeRequired: 'Total Income Required',
  TotalMonthlyGrossIncome: 'Total Monthly Gross Income',
  TotalShortfall: 'Total Shortfall',
  PledgeUnpledgeCombination: 'Pledge / Unpledge Combination',
  PledgeAmount: 'Pledge Amount',
  Pledging: 'Pledging',
  IfPledging: 'If Pledging',
  AndUnpledging: 'And Unpledging',
  IfUnpledging: 'If Unpledging',
  NotRequired: 'Not Required',
};

export enum PropertyType {
  RESIDENTIAL = 'Residential',
  NON_RESIDENTIAL = 'Non-Residential',
}

export enum PropertySubType {
  CONDO_LANDED = 'Condo / Landed',
  HDB_EC = 'HDB / EC',
}

export enum PledgeUnpledgeCombination {
  YES = 'Yes',
  NO = 'No',
}

export const MEDIUM_TERM_INTEREST_RATE = {
  MIN: calculatorConfig.medium_term_interest_rate.min,
  MAX: calculatorConfig.medium_term_interest_rate.max,
  DEFAULT: calculatorConfig.medium_term_interest_rate.default,
  LABEL_INPUTS: generateSliderSteps(
    calculatorConfig.medium_term_interest_rate.min,
    calculatorConfig.medium_term_interest_rate.max,
    0.2,
  ),
  STEP: 0.01,
};

export const RESIDENTIAL_CONDO_65_DATA = {
  LOAN_TENURE: {
    MIN: 1,
    MAX: config.max_loan_tenure_in_years.private_residential.upto_65_financing_year,
    DEFAULT: config.max_loan_tenure_in_years.private_residential.upto_65_financing_year,
    LABEL_INPUTS: generateSliderSteps(
      1,
      config.max_loan_tenure_in_years.private_residential.upto_65_financing_year,
      10,
    ),
    STEP: 1,
  },
  LOAN_TO_VALUE: {
    MIN: 1,
    MAX: config.max_ltv_in_percent.private_residential.first_loan.upto_65_financing_year,
    DEFAULT: config.max_ltv_in_percent.private_residential.first_loan.upto_65_financing_year,
    LABEL_INPUTS: generateSliderSteps(
      1,
      config.max_ltv_in_percent.private_residential.first_loan.upto_65_financing_year,
      15,
    ),
    STEP: 1,
  },
  MAX_ASSESSABLE_RATE: config.tdsr_rate,
};

export const RESIDENTIAL_CONDO_75_DATA = {
  LOAN_TENURE: {
    MIN: 1,
    MAX: config.max_loan_tenure_in_years.private_residential.upto_75_financing_year,
    DEFAULT: 30,
    LABEL_INPUTS: generateSliderSteps(
      1,
      config.max_loan_tenure_in_years.private_residential.upto_75_financing_year,
      10,
    ),
    STEP: 1,
  },
  LOAN_TO_VALUE: {
    MIN: 1,
    MAX: config.max_ltv_in_percent.private_residential.first_loan.upto_75_financing_year,
    DEFAULT: config.max_ltv_in_percent.private_residential.first_loan.upto_75_financing_year,
    LABEL_INPUTS: generateSliderSteps(
      1,
      config.max_ltv_in_percent.private_residential.first_loan.upto_75_financing_year,
      15,
    ),
    STEP: 1,
  },
  MAX_ASSESSABLE_RATE: config.tdsr_rate,
};

const maxLoanTenureForHdbEc = Math.max(
  config.max_loan_tenure_in_years.hdb.bank_loan.upto_65_financing_year,
  config.max_loan_tenure_in_years.hdb.bank_loan.upto_75_financing_year,
  config.max_loan_tenure_in_years.hdb.hdb_loan,
  config.max_loan_tenure_in_years.ec.upto_65_financing_year,
  config.max_loan_tenure_in_years.ec.upto_75_financing_year,
);

export const RESIDENTIAL_HDB_EC_DATA = {
  LOAN_TENURE: {
    MIN: 1,
    MAX: maxLoanTenureForHdbEc,
    DEFAULT: config.max_loan_tenure_in_years.hdb.hdb_loan,
    LABEL_INPUTS: generateSliderSteps(1, maxLoanTenureForHdbEc, 10),
    STEP: 1,
  },
  LOAN_TO_VALUE: {
    MIN: 1,
    MAX: config.max_ltv_in_percent.hdb.upto_65_financing_year,
    DEFAULT: config.max_ltv_in_percent.hdb.upto_65_financing_year,
    LABEL_INPUTS: generateSliderSteps(1, config.max_ltv_in_percent.hdb.upto_65_financing_year, 15),
    STEP: 1,
  },
  MAX_ASSESSABLE_RATE: config.msr_rate,
};

export const NON_RESIDENTIAL_DATA = {
  LOAN_TENURE: {
    MIN: 1,
    MAX: config.max_loan_tenure_in_years.commercial_industrial,
    DEFAULT: config.max_loan_tenure_in_years.commercial_industrial,
    LABEL_INPUTS: generateSliderSteps(1, config.max_loan_tenure_in_years.commercial_industrial, 10),
    STEP: 1,
  },
  LOAN_TO_VALUE: {
    MIN: 1,
    MAX: config.max_ltv_in_percent.commercial_industrial,
    DEFAULT: config.max_ltv_in_percent.commercial_industrial,
    LABEL_INPUTS: generateSliderSteps(1, config.max_ltv_in_percent.commercial_industrial, 15),
    STEP: 1,
  },
  MAX_ASSESSABLE_RATE: config.tdsr_rate,
};
